import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { AnimatedLoading } from '@actual-app/components/icons/AnimatedLoading';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { useNavigate } from '#hooks/useNavigate';

import { Title } from './common';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 60000;

// Paystack redirects here after checkout. Payment confirmation arrives via
// webhook, so we poll until the subscription flips to active.
export function SubscribeComplete() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [state, setState] = useState<'waiting' | 'active' | 'slow'>('waiting');
  const startedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      while (!cancelled) {
        const user = await send('saas-get-user');
        if (cancelled) return;

        if (user?.subscription?.status === 'active') {
          setState('active');
          return;
        }
        if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
          setState('slow');
        }
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    }
    void poll();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <View style={{ maxWidth: 450, marginTop: -30, color: theme.pageText }}>
      <Title text={t('Thank you!')} />

      {state === 'active' ? (
        <>
          <Text style={{ fontSize: 16, marginBottom: 20 }}>
            <Trans>
              Your Pesa Wise subscription is active. Enjoy budgeting without
              limits — your data now syncs across all your devices.
            </Trans>
          </Text>
          <Button
            variant="primary"
            style={{ padding: 10, fontSize: 15 }}
            onPress={() => navigate('/')}
          >
            <Trans>Open Pesa Wise</Trans>
          </Button>
        </>
      ) : (
        <>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              marginBottom: 15,
            }}
          >
            <AnimatedLoading style={{ width: 20, height: 20 }} />
            <Text style={{ fontSize: 16 }}>
              <Trans>Confirming your payment…</Trans>
            </Text>
          </View>
          {state === 'slow' && (
            <Text style={{ fontSize: 14, color: theme.pageTextLight }}>
              <Trans>
                This is taking longer than usual. Your subscription will
                activate automatically as soon as the payment is confirmed — you
                can safely leave this page and keep using the app.
              </Trans>
            </Text>
          )}
        </>
      )}
    </View>
  );
}
